import React, { useState } from 'react'
import { MapContainer, TileLayer, useMapEvents } from 'react-leaflet'
import { Paper, Typography, Box, Button, Skeleton, CircularProgress } from '@mui/material'
import ListingMarker from './ListingMarker'
import { useListingsByLocationFetcher } from './useListingsFetcherByView'

const MapEventsHandler = ({ onBoundsChange }) => {
  useMapEvents({
    moveend: (event) => {
      const map = event.target
      const bounds = map.getBounds()
      onBoundsChange(bounds)
    },
  })
  return null
}

const StandaloneListingsMapView = ({ priceRange }) => {
  const [currentBounds, setCurrentBounds] = useState(null)
  const { listings, loading, hasMore, loadNextPage } = useListingsByLocationFetcher({
    bounds: currentBounds,
    priceRange,
    limit: 250,
  })
  const [mapReady, setMapReady] = useState(false)

  const handleBoundsChange = (bounds) => {
    setCurrentBounds(bounds)
  }

  return (
    <Paper
      sx={{
        height: 600,
        width: '100%',
        mt: 2,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {!mapReady && (
        <Box
          sx={{
            position: 'absolute',
            zIndex: 999,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'rgba(255,255,255,0.7)',
          }}
        >
          <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
        </Box>
      )}

      <MapContainer
        center={[52.3, 5.5]} // Better centered on Holland
        zoom={8} // Zoomed out to show more of Holland initially
        style={{ height: '100%', width: '100%' }}
        whenReady={(mapEvent) => {
          setMapReady(true)
          const bounds = mapEvent.target.getBounds()
          setCurrentBounds(bounds)
        }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />
        <MapEventsHandler onBoundsChange={handleBoundsChange} />
        {listings.map(
          (listing) =>
            typeof listing.latitude === 'number' &&
            typeof listing.longitude === 'number' && (
              <ListingMarker key={listing.id} listing={listing} />
            )
        )}
      </MapContainer>

      {/* Status indicators */}
      <Box sx={{ position: 'absolute', bottom: 16, right: 16, display: 'flex', gap: 2 }}>
        {hasMore && !loading && (
          <Button variant="contained" onClick={loadNextPage}>
            Load More
          </Button>
        )}

        {/* Optional: Show count of listings */}
        <Box
          sx={{
            bgcolor: 'rgba(255,255,255,0.8)',
            px: 2,
            py: 1,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2">{listings.length} listings shown</Typography>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)' }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Fallback message if listings exist but none have valid coordinates */}
      {listings.length > 0 &&
        listings.filter((l) => typeof l.latitude === 'number' && typeof l.longitude === 'number')
          .length === 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'white',
              p: 3,
              borderRadius: 1,
              boxShadow: 3,
              zIndex: 1000,
              maxWidth: '80%',
            }}
          >
            <Typography variant="body1">
              Listings exist but none have valid map coordinates.
            </Typography>
          </Box>
        )}
    </Paper>
  )
}

export default StandaloneListingsMapView
